/**
 * PDApp awareness for the share viewer (#757) — the pure decisions, kept out of
 * the chrome so each one can be pinned by a unit test without mounting it.
 *
 * A share recipient reaches a PDApp (a Mode-B `.pdapp` folder; internal
 * identifiers say "miniapp") two ways:
 *
 *   • the share ROOT is the PDApp — the link was minted on the `.pdapp` folder;
 *   • the share root is a PARENT folder and the recipient opens a nested PDApp
 *     from the listing / tree — carried in the URL as `?pdapp=<folderId>` so
 *     Back, reload and deep links all keep the mode.
 *
 * Either way the viewer enters "PDApp mode": the Explorer + viewer layout with
 * the panel labelled "PDApp" and Run / Settings controls, landing on the APP
 * TAB (the app running full-bleed) the way the in-app workspace does
 * (WorkspaceView's auto-open → MiniAppEditor's default 'run' face).
 *
 * Nothing here grants anything. Run opens the app's index.html through the
 * SAME share sub-session path an HTML app in a shared folder already takes —
 * the preview server keys Mode B (app-principal elevation + per-email consent)
 * off the served `.pdapp` root, and the chrome's existing silent bind-account
 * supplies the recipient identity. Settings is only offered when the recipient
 * natively holds write on the PDApp in their own drive — see `pdappCanManage`.
 */
import { isMiniappFolder } from '@papan/types'

/** The subset of `/info` the PDApp decision reads. */
export interface PdappInfoShape {
  file: { isFolder: boolean }
  share: { rootFileId: string; rootName: string | null; rootIsFolder: boolean; rootIsMount?: boolean }
}

/** The share ROOT is itself a PDApp folder. */
export function shareRootIsPdapp(info: PdappInfoShape | null | undefined): boolean {
  if (!info || !info.share.rootIsFolder || !info.share.rootName) return false
  // A POINTER root (`.shared` / `.chain`) is a normal folder here, whatever it opens — no PDApp
  // mode, favicon or app tile (owner 2026-10-03); its own name says nothing about its target.
  if (info.share.rootIsMount) return false
  return isMiniappFolder(info.share.rootName)
}

/**
 * Which PDApp folder (if any) the viewer is in "PDApp mode" for.
 *
 *   • a PDApp ROOT always wins — the whole share is the app, so there is no
 *     listing to fall back to and `?pdapp=` is meaningless;
 *   • otherwise a nested `?pdapp=<id>` — honoured ONLY on the folder session
 *     (the one branch that renders the listing + in-place overlay). On a direct
 *     sub-file session the param is ignored, exactly like `?file=` is.
 *
 * The id is NOT trusted as an authorization: every read it drives goes through
 * the share session's own `/list` + `/sub-session`, which scope-check it
 * server-side (a hand-edited id outside the share 403s there).
 */
export function resolvePdappFolderId(
  info: PdappInfoShape | null | undefined,
  pdappParam: string | null,
): string | null {
  if (!info) return null
  if (shareRootIsPdapp(info)) return info.share.rootFileId
  if (pdappParamTakesEffect(info) && pdappParam) return pdappParam
  return null
}

/**
 * Whether setting `?pdapp=` changes anything on this session — the rule above,
 * named so a caller deciding "open it in PDApp mode, or navigate instead" asks
 * the SAME question (#788's launch: setting a param that is ignored does
 * nothing, silently).
 */
export function pdappParamTakesEffect(info: PdappInfoShape | null | undefined): boolean {
  return !!info && !shareRootIsPdapp(info) && info.file.isFolder
}

/**
 * The PDApp's entry — its root-level `index.html` (case-insensitive), the file
 * MiniAppEditor's Run face runs. `null` when the app has none (a triggers-only
 * app): Run is then disabled rather than guessing another HTML file.
 */
export function findPdappEntry(files: ReadonlyArray<{ id: string; name: string; isFolder: boolean }>): string | null {
  return files.find((f) => !f.isFolder && f.name.toLowerCase() === 'index.html')?.id ?? null
}

/** One page of the share session's `/list` for a folder — the fields the
 *  entry lookup reads. `breadcrumbs` runs share root → the listed folder. */
export interface PdappListPage {
  files: ReadonlyArray<{ id: string; name: string; isFolder: boolean }>
  hasMore?: boolean
  /** Raw resume position when the server's bounded scan stopped short (pass as `cursor=`). */
  nextCursor?: number | null
  breadcrumbs?: ReadonlyArray<{ id: string; name: string }>
}

/**
 * The outcome of looking a PDApp up (#757 review):
 *   • `ready`     — it is a PDApp; `entryId` is its index.html, or null when it
 *                   genuinely has none (every page was read);
 *   • `not-pdapp` — a nested `?pdapp=<id>` that names a folder which is NOT a
 *                   `.pdapp` (a hand-edited / stale URL): the viewer drops back
 *                   to the normal folder view instead of dressing it as an app;
 *   • `error`     — the lookup itself failed (403 / 5xx / network). NOT "no
 *                   index.html": the recipient is offered a retry, never told
 *                   the app has nothing to run.
 */
export type PdappEntryLookup =
  | { status: 'ready'; entryId: string | null; name: string | null }
  | { status: 'not-pdapp' }
  | { status: 'error' }

/** Page size for the entry lookup — the server's `/list` ceiling. */
export const PDAPP_LIST_PAGE = 500
/** Hard stop on paging (500 × 40 = 20k root entries) so a pathological folder
 *  can't spin the lookup forever; past it the app reads as having no entry. */
const PDAPP_LIST_MAX_PAGES = 40

/**
 * Find a PDApp's entry by paging its root listing until `index.html` turns up
 * (a big app root must not hide it past the first page), and — for a NESTED
 * `?pdapp=` — confirm from the listing's own breadcrumb that the folder really
 * is a `.pdapp` (the share session has no per-id name lookup; the breadcrumb's
 * last crumb IS the listed folder). A PDApp ROOT was already confirmed by name
 * from `/info`, so it skips the check.
 *
 * `fetchPage` throws (or rejects) on a non-OK response — any throw is `error`.
 */
export async function lookupPdappEntry(
  folderId: string,
  fetchPage: (offset: number, cursor?: number | null) => Promise<PdappListPage>,
  opts: { verifyIsPdapp: boolean },
): Promise<PdappEntryLookup> {
  try {
    let offset = 0
    let cursor: number | null = null
    let name: string | null = null
    for (let page = 0; page < PDAPP_LIST_MAX_PAGES; page++) {
      const body = await fetchPage(offset, cursor)
      if (page === 0) {
        const crumbs = body.breadcrumbs ?? []
        const self = crumbs.find((c) => c.id === folderId) ?? crumbs[crumbs.length - 1]
        name = self?.name ?? null
        if (opts.verifyIsPdapp && (!name || !isMiniappFolder(name))) return { status: 'not-pdapp' }
      }
      const entryId = findPdappEntry(body.files)
      if (entryId) return { status: 'ready', entryId, name }
      if (!body.hasMore) break
      // The server's resume position when it gives one (a bounded scan that stopped
      // short — the page may even be empty), else offset paging.
      if (typeof body.nextCursor === 'number') cursor = body.nextCursor
      else if (body.files.length === 0) break
      else offset += body.files.length
    }
    return { status: 'ready', entryId: null, name }
  } catch {
    return { status: 'error' }
  }
}

/** Permission levels that can manage a PDApp (mirrors the share explorer's canWritePerm). */
const WRITE_LEVELS = new Set(['readwrite', 'admin', 'owner'])

/**
 * Whether the recipient may open the PDApp's Settings tab in the share viewer.
 *
 * A PDApp's settings (access grants, triggers, consent, icon) live behind
 * `/api/pdapp/*`, which is WRITE-gated (`requireMiniappManage`) and does not
 * accept a share session at all — a link grant cannot confer it, and this adds
 * no server authority. The Settings tab therefore renders the real settings
 * surface (`MiniAppSettingsModifier`) calling those routes AS THE SIGNED-IN
 * RECIPIENT (their own cookie session + the app's active tenant), and is
 * offered only when the share resolver's `resolve-for-recipient` answer for the
 * PDApp folder — asked under that SAME identity — says they natively hold write
 * on it. The server re-runs its manage gate on every call regardless.
 *
 * The drive URL must address the PDApp folder itself (`…/folder/<id>`): a
 * `.shared` mount match comes back as a different (mount / compound) id — the
 * settings routes would be asked about an id that isn't the app — and the
 * resolver reports a non-write placeholder permission for it anyway.
 */
export function pdappCanManage(
  resolver: { accessibleInActiveTenant?: boolean; driveUrl?: string; permission?: string } | null | undefined,
  pdappFolderId: string,
): boolean {
  if (!resolver?.accessibleInActiveTenant || !resolver.driveUrl) return false
  if (!resolver.permission || !WRITE_LEVELS.has(resolver.permission)) return false
  return resolver.driveUrl.endsWith(`/folder/${pdappFolderId}`)
}

/**
 * The PDApp tab showing — the share-view twin of MiniAppEditor's two faces
 * (`requestMiniappView(folderId, 'run' | 'settings')` in the app):
 *   • 'run'      — the APP TAB: the app itself running full-bleed (no HTML
 *                  source/preview editor chrome), the entry opened through the
 *                  share session's app-principal path;
 *   • 'settings' — the SETTINGS TAB (signed-in native managers only).
 * Absent (null) — a plain file open from the PDApp panel, in its own editor.
 */
export type PdappFace = 'run' | 'settings'

/** Read the face from the URL param; anything else is "no face". */
export function parsePdappFace(v: string | null | undefined): PdappFace | null {
  return v === 'run' || v === 'settings' ? v : null
}

/** URL param names the PDApp mode owns. `view=run|settings` = the PDApp tab. */
export const PDAPP_PARAM = 'pdapp'
export const VIEW_PARAM = 'view'

/**
 * Does the Settings gate's stored answer apply RIGHT NOW? Only for the PDApp and
 * the identity (tenant + org email) it was asked under — so the render in which
 * the viewer switches identity can't mount Settings as the new identity on the
 * old identity's approval (review #757 round 3).
 */
export function pdappGateHolds(
  answer: { folderId: string; tenantId: string | null; orgEmail: string | null } | null | undefined,
  pdappFolderId: string,
  tenantId: string | null,
  orgEmail: string | null,
): boolean {
  return !!answer && answer.folderId === pdappFolderId && answer.tenantId === tenantId && answer.orgEmail === orgEmail
}
